import { isEmpty, isEqual, pickBy } from 'lodash-es'
import { APPSTATE, LAYOUTSTATE, PMSTATE, SETTINGSSTATE, VIDEOSTATE } from '..'
import type { Size } from '../../../types/mfc'
import type {
    FloaterLocation,
    FloaterLocations,
    FloaterModal,
    FloatingRoomModal,
    MiniPlayerOrigin,
} from '../../../types/state'
import {
    clearPMUnreadCount,
    loadMessageHistory,
    openMessage,
    resumePMUnreadCount,
} from '../../actions/message'
import { messageAction } from '../../actions/workerActions'
import { COMPONENT_HEIGHTS, COMPONENT_WIDTHS } from '../../components/theme'
import {
    FloaterSize,
    MessageAction,
    ModalKind,
    RoomView,
    SettingsType,
    UserEvent,
    ViewMode,
} from '../../constants/enums'
import {
    FLOATING_PREVIEW,
    FLOATING_ROOM,
    MINI_PLAYER,
    MY_WEBCAM,
    QUICK_REPLY,
} from '../../constants/floaters'
import { BROADCASTING_STATE } from '../../constants/myWebcam'
import { avatarUrl } from '../../mfcLogic/avatarPreviewUrl'
import { getLens } from '../../mfcLogic/lens'
import { Metadata, setRecipient } from '../../mfcLogic/pm'
import { isTouchDevice } from '../../utilities/browser'
import type { Edge } from '../../utilities/shrinkToEdge'
import {
    getModalStack,
    getModalState,
    getRoomsState,
    getScreenState,
    getSlugAndId,
    getUser,
} from '../getState'
import { recalcRoom } from '../internal/floaters'
import {
    canViewerWatchMemberCams,
    isMemberCam,
    isMemberWebcamRoom,
    isOwnWebcamRoom,
    pmPreview,
} from '../internal/memberVideo'
import {
    closeFloatingPreview,
    generateModalKey,
    getFloaterOrientation,
    getNewExpandLocation,
    PREVIEW_SIZES,
    removeMultiModal,
    setModal,
    setModalStack,
    setMultiModal,
    setShowMultiModal,
} from '../internal/modals'
import { resolveOrientationViewMode } from '../internal/orientationViewMode'
import { setRoomViewMode } from '../internal/rooms'
import { pubSubUsers } from '../pubSub'
import { stopMyWebcam } from '../state.app'
import { closeRoom } from '../state.rooms'
import { initVideo } from '../state.video'
import { openStopUserBroadcastingConfirmation } from './state.confirmation'
import { openSignupDrawer, openTokensDrawer } from './state.drawer'

export const closeFloater = (id: number, type: string) => {
    // console.log('[closeFloater]', id, type)

    // if a user closes my webcam, prompt them to stop broadcasting
    if (type === MY_WEBCAM) {
        if (
            APPSTATE.myWebcam?.broadcasting === BROADCASTING_STATE.ON ||
            APPSTATE.myWebcam?.broadcasting === BROADCASTING_STATE.OFF
        ) {
            openStopUserBroadcastingConfirmation(stopMyWebcam, true)
        }
    }
    if (type === QUICK_REPLY) {
        resumePMUnreadCount(id)
        updateLocalStorage(id, { open: false })
    }
    if (type === FLOATING_PREVIEW) {
        // setLargePreviewPinnedIDState(null)
        closeFloatingPreview(id)
        return
    }
    if (type === MINI_PLAYER) {
        const modalKey = generateModalKey(MINI_PLAYER, id)
        const origin = getModalState().floater[modalKey]?.origin
        removeMultiModal(modalKey)
        // a room-origin player is the minimized room: closing it leaves
        if (origin?.kind === 'room') closeFloatingRoom(origin.roomID)
        return
    }
    setShowMultiModal(generateModalKey(type, id), false, true)
}

export const closeTopFloater = () => {
    const floaters = getModalState().floater
    const foundModalKey = getModalStack().byPosition.find(
        // keyed floaters; MY_WEBCAM is likely parked at an edge already
        (key) => key.includes('-') && floaters[key]?.show,
    )
    const { show, minimized, viewMode } = getModalState().floating_room
    // a minimized room isn't on screen, so its view mode doesn't guard
    const roomInFullWindow =
        show && !minimized && viewMode === ViewMode.FullWindow
    if (!foundModalKey || roomInFullWindow) return false
    const floater = floaters[foundModalKey]
    closeFloater(floater.id, floater.type)
    return true
}
const DEFAULT_LOCATION = {
    x: 40,
    y: 40,
    speedX: 0,
    speedY: 0,
    width: 0,
}

const DEFAULT_PREVIEW_STATE = {
    // id,
    minimized: false,
    // active: true,
    locations: {
        portrait: {
            x: 8,
            y: 8,
            speedX: 0,
            speedY: 0,
            width: 0,
        },
        landscape: {
            x: 8,
            y: 8,
            speedX: 0,
            speedY: 0,
            width: 0,
        },
    },
    size: FloaterSize.Small,
    sticky: false, // room preview or not (will stay open on nav)
    stickyHeader: false, // shrink/grow controls
    close: false, // close button
}

export const getTopFloaterLocation = () => {
    // check the stack for the first floater modal key and return its x,y position, otherwise return 0,0
    const foundModalKey = getModalStack().byPosition.find((key) => {
        // 1) check what we're dealing with
        // - if it's a multimodal (floater) then we found something
        if (key.includes('-')) return true
        // - MY_WEBCAM is probably not usable as it's likely to be placed at the edge already
        // - otherwise go to the next one
        return false
    })

    if (foundModalKey) {
        const { locations } = getModalState().floater[foundModalKey]
        if (locations) {
            const [orientation] = getFloaterOrientation()
            const location = locations[orientation] || {}
            return { x: location.x ?? 0, y: location.y ?? 0 }
        }
    }
    return { x: 0, y: 0 }
}

export const calcPreviewLocation = (
    size: FloaterSize,
    windowSize?: Size,
): FloaterLocation => {
    const [, portrait] = getFloaterOrientation()
    const offsetX = !portrait
        ? LAYOUTSTATE.layout.expandedNav
            ? COMPONENT_WIDTHS.leftNavExpanded
            : COMPONENT_WIDTHS.leftNav
        : 8
    const { windowHeight, windowWidth } = getScreenState()
    const foundSize = windowSize ?? { width: windowWidth, height: windowHeight }
    const calcWidth = PREVIEW_SIZES[size]()
    const videoHeight = (calcWidth / 16) * 9
    const previewHeight = videoHeight + (size > FloaterSize.Medium ? 64 : 0)
    const offsetY = `${
        Math.min((foundSize.height - previewHeight) / foundSize.height, 0.7) *
        100
    }%`
    // get around the fact that `FloaterLocation` should be a number,
    // but we use a percentage for initial Y positioning
    return {
        x: offsetX,
        y: offsetY as any,
        width: calcWidth,
        height:
            size > FloaterSize.Small
                ? previewHeight + COMPONENT_HEIGHTS.chatHeader
                : previewHeight,
    }
}

/** Close all floating previews */
export const clearAllPreviews = () => {
    Object.values(getModalState().floater).forEach((modal: FloaterModal) => {
        if (modal.show && modal.type === FLOATING_PREVIEW && !modal.close)
            closeFloatingPreview(modal.id)
    })
}

export const setFloatingPreview = (
    idOrSlug: number | string,
    state: FloaterModal,
    location?: FloaterLocation,
) => {
    const [, id] = getSlugAndId(idOrSlug)
    const modalKey = generateModalKey(FLOATING_PREVIEW, id)
    const existingPreview = getModalState().floater[modalKey]

    const [orientation] = getFloaterOrientation()

    const newLocation = location && {
        [orientation]: location,
    }
    const newPreview = {
        id,
        ...DEFAULT_PREVIEW_STATE,
        ...existingPreview,
        ...state,
        type: FLOATING_PREVIEW,
        locations: {
            ...DEFAULT_PREVIEW_STATE.locations,
            ...existingPreview?.locations,
            ...newLocation,
        },
    }

    setMultiModal(modalKey, newPreview, {
        immediate: true,
    })
}

export const hideFloatingPreview = (toRoom = false) => {
    const existingPreviews = Object.values(getModalState().floater).filter(
        (floater: FloaterModal) =>
            floater.type === FLOATING_PREVIEW &&
            floater.show &&
            (toRoom ? true : !floater.close),
    )
    existingPreviews.forEach((existingPreview) => {
        closeFloatingPreview(existingPreview.id)
    })
}

const getSafeSize = (size: FloaterSize, increase: boolean) => {
    if (getScreenState().windowWidth < 1025) {
        // skip the "Medium" size on smaller screens
        if (size === FloaterSize.Medium)
            return increase ? FloaterSize.Large : FloaterSize.Small
    }
    return size
}
export const setFloaterSize = (
    idOrSlug: number | string,
    floaterType: string,
    size: FloaterSize,
) => {
    const [, id] = getSlugAndId(idOrSlug)
    const modalKey = generateModalKey(floaterType, id)
    const floater = getModalState().floater[modalKey]
    const [orientation, portrait] = getFloaterOrientation()
    const increased = size > floater.size
    const foundSize = getSafeSize(size, increased)
    const [newLocation] = getNewExpandLocation(
        modalKey,
        floaterType,
        foundSize,
        floater.locations[orientation],
    )
    setMultiModal(
        modalKey,
        {
            ...floater,
            type: floaterType,
            sticky: floaterType === QUICK_REPLY ? portrait : floater.sticky,
            locations: {
                ...floater.locations,
                [orientation]: newLocation,
            },
            size: foundSize,
        },
        { immediate: true },
    )
}

export const isModelVideoPublic = (id: number) => {
    const {
        isBroadcasting,
        isTruePrivate,
        isPrivateShow,
        isGroupShow,
        isClubShow,
        isOffline,
        isAway,
        banned,
        isKicked,
    } = getUser(id)
    if (!isBroadcasting) return false
    const inHiddenShow =
        isTruePrivate || isPrivateShow || isGroupShow || isClubShow
    const noShowType = isOffline || inHiddenShow || isAway
    return !noShowType && !banned && !isKicked
}

export const openQuickReply = (
    idOrSlug: number | string,
    overrideOptions?: Partial<FloaterModal>,
    location?: FloaterLocation,
) => {
    const [slug, id] = getSlugAndId(idOrSlug)

    if (!PMSTATE.chats[id]?.isHistoryInitiated) {
        loadMessageHistory(id)
    }

    const modalKey = generateModalKey(QUICK_REPLY, id)

    const options = {
        size: isTouchDevice
            ? FloaterSize.Small
            : isModelVideoPublic(id)
              ? FloaterSize.Large
              : FloaterSize.Small,
        ...overrideOptions,
    }
    if (!options.minimized) {
        clearPMUnreadCount(id)
    }
    const [orientation, portrait] = getFloaterOrientation()
    const otherOrientation = portrait ? 'landscape' : 'portrait'
    const sticky = portrait
    const isMobile = APPSTATE.app.isMobile

    if (!portrait && isMobile) {
        // we should create this minimized and then actually route to the full pm room experience.
        // this will handle landscape android phones and tablets, and ios phones
        // - all those devices are fine in portrait but landscape is too crushed
        // - ipad seems to handle landscape fine with its virtual keyboard handling
        setMultiModal(
            modalKey,
            {
                id,
                minimized: true,
                show: true,
                type: QUICK_REPLY,
                locations: {
                    [orientation]: { ...DEFAULT_LOCATION },
                    [otherOrientation]: { ...DEFAULT_LOCATION },
                } as FloaterLocations,
                sticky,
                size: isTouchDevice
                    ? FloaterSize.Small
                    : isModelVideoPublic(id)
                      ? FloaterSize.Large
                      : FloaterSize.Small,
                ...options,
            },
            { skipStack: true },
        )
        setModalStack({ modalKey }, { pushDown: true })

        // let this handle the rest too (clearing unread, setting recipient, etc)
        openMessage(slug)
        updateLocalStorage(slug)
        return
    }

    if (isMobile) {
        const pms = PMSTATE.chats
        // check to make sure chat head based quick replies are
        // limited to 2 at a time with `show` true
        // - show:false the one with the oldest last message?
        const existingQuickReplies = Object.values(
            getModalState().floater,
        ).filter(
            (floater: FloaterModal) =>
                floater.show && floater.type === QUICK_REPLY,
        )

        if (existingQuickReplies.length > 1) {
            existingQuickReplies.sort((a, b) => {
                const messagesA = pms[a.id]
                const aTimestamp =
                    messagesA?.messages?.[messagesA?.messages?.length - 1]
                        ?.timestamp ?? 0
                const messagesB = pms[b.id]
                const bTimestamp =
                    messagesB?.messages?.[messagesB?.messages?.length - 1]
                        ?.timestamp ?? 0
                return bTimestamp - aTimestamp
            })
            const quickReplyToClose =
                existingQuickReplies[existingQuickReplies.length - 1]

            setShowMultiModal(
                generateModalKey(QUICK_REPLY, quickReplyToClose.id),
                false,
                false,
            )
        }
    }

    const existingModal = getModalState().floater[modalKey]
    if (existingModal && existingModal.show) {
        const [newLocation] = getNewExpandLocation(modalKey, QUICK_REPLY)
        setMultiModal(
            modalKey,
            {
                ...existingModal,
                minimized: false,
                sticky,
                locations: {
                    [orientation]: { ...newLocation },
                    [otherOrientation]: { ...newLocation },
                } as FloaterLocations,
                ...options,
            },
            { immediate: true },
        )
        updateLocalStorage(slug)
        return
    }

    // get the location of the highest position floater in the stack and use its location
    const activeFloaterLocation = getTopFloaterLocation()
    const startingLocation = {
        x: activeFloaterLocation.x + 40,
        y: activeFloaterLocation.y + 40,
        speedX: 0,
        speedY: 0,
    }

    updateLocalStorage(slug, {
        open: true,
        lastInteraction: +new Date(),
        location: location ?? startingLocation,
        minimized: !!options?.minimized,
    })
    const [newLocation] = getNewExpandLocation(
        modalKey,
        QUICK_REPLY,
        options.size,
        location ?? startingLocation,
    )
    setMultiModal(
        modalKey,
        {
            id,
            type: QUICK_REPLY,
            minimized: false,
            show: true,
            locations: {
                [orientation]: { ...newLocation },
                [otherOrientation]: { ...newLocation },
            } as FloaterLocations,
            sticky,
            ...options,
        },
        { immediate: true, skipStack: true },
    )
    setModalStack({ modalKey })
}

export const openMyWebcamFloater = (header?) => {
    getLens().MyWebcam_Click()
    // the owner's own room already shows the camera
    const { floating_room } = getModalState()
    if (
        floating_room.show &&
        !floating_room.minimized &&
        floating_room.roomView === RoomView.Room &&
        !floating_room.hideVideo &&
        isOwnWebcamRoom(floating_room.id)
    ) {
        setModalStack({ modalKey: FLOATING_ROOM })
        return
    }
    const existingModal =
        getModalState().floater[MY_WEBCAM] ?? ({} as FloaterModal)
    const [orientation, isPortrait] = getFloaterOrientation()
    const otherOrientation = isPortrait ? 'landscape' : 'portrait'
    const [newLocation] = getNewExpandLocation(
        MY_WEBCAM,
        MY_WEBCAM,
        FloaterSize.Medium,
        {
            x: header ? getScreenState().windowWidth - 375 : 60,
            speedX: 0,
            speedY: 0,
            y: 40,
        },
    )

    setMultiModal(MY_WEBCAM, {
        ...existingModal,
        type: MY_WEBCAM,
        show: true,
        minimized: false,
        locations: {
            [orientation]: { ...newLocation },
            [otherOrientation]: { ...newLocation },
        } as FloaterLocations,
        close: true,
        size: FloaterSize.Large,
        stickyHeader: true,
        // id: 0,
    })
    setModalStack({ modalKey: MY_WEBCAM })
}

export const updateLocalStorage = (
    idOrSlug: string | number,
    quickReplyData?: Partial<Metadata['quickReply']>,
) => {
    const [slug, id] = getSlugAndId(idOrSlug)
    const modalKey = generateModalKey(QUICK_REPLY, id)
    const quickReply = getModalState().floater[modalKey]
    const [orientation] = getFloaterOrientation()

    // console.trace('[updateLocalStorage]', slug, {
    //     open: quickReply?.show ?? false,
    //     lastInteraction: +new Date(),
    //     minimized: quickReply?.state?.minimized,
    //     location: quickReply?.state?.locations?.[orientation],
    //     ...quickReplyData,
    // })

    setRecipient(slug, {
        quickReply: {
            open: quickReply?.show ?? false,
            lastInteraction: +new Date(),
            minimized: quickReply?.minimized,
            location: quickReply?.locations?.[orientation],
            ...quickReplyData,
        },
    })
}

/**
 * Close the quick reply if we're Landscape and Mobile -
 * The quick replies don't actually show up anywhere but the left Nav,
 * without a way to close them in that situation
 */
export const checkAndCloseQuickReply = (idOrSlug: string | number) => {
    const [, portrait] = getFloaterOrientation()

    const isMobile = APPSTATE.app.isMobile

    const [slug, id] = getSlugAndId(idOrSlug)

    if (!portrait && isMobile) {
        const modalKey = generateModalKey(QUICK_REPLY, id)
        const existingModal = getModalState().floater[modalKey]
        if (existingModal) {
            updateLocalStorage(slug, { open: false })
            setShowMultiModal(modalKey, false)
        }
    }
}

// export const isQuickReplyOpen = (id: number, checkMinimized?: boolean) => {
//     const quickReply =
//         getModalState().floater[generateModalKey(QUICK_REPLY, id)]
//     if (checkMinimized) return quickReply?.show && !quickReply?.minimized
//     return quickReply?.show
// }

export const updateFloaterLocation = (
    id: number,
    type: string,
    location: FloaterLocation,
    skipEmit = true,
) => {
    // avoid emitting since this is just data we store for spawning purposes
    const floater = getModalState().floater[generateModalKey(type, id)]
    if (floater) {
        const [orientation] = getFloaterOrientation()
        const currentLocation = floater.locations[orientation]
        if (!currentLocation) return
        const speedY = location.y - currentLocation.y
        // ensure we have a minimum x speed
        const speedX = location.x - currentLocation.x

        const newLocation = {
            x: location.x ?? currentLocation.x,
            y: location.y ?? currentLocation.y,
            speedX: isNaN(speedX) ? 0 : speedX,
            speedY: isNaN(speedY) ? 0 : speedY,
            width: currentLocation.width,
            height: currentLocation.height,
        }

        setMultiModal(
            generateModalKey(type, id),
            {
                ...floater,
                type,
                locations: {
                    ...floater.locations,
                    [orientation]: newLocation,
                },
            },
            { skipEmit, skipStack: true },
        )
    }
}

export const minimizeFloater = (id: number, type: string, minimize = true) => {
    const modalKey = generateModalKey(type, id)
    const existingModal = getModalState().floater[modalKey]

    if (!existingModal) return
    if (type === QUICK_REPLY) {
        const [slug] = getSlugAndId(id)
        if (minimize) {
            resumePMUnreadCount(id)
        } else {
            clearPMUnreadCount(id)
        }

        updateLocalStorage(slug, { minimized: minimize })

        const floatingRoomState = getModalState().floating_room
        if (getModalState().floating_room.show && floatingRoomState.id === id) {
            // ensure we dont show video if we're in the room already
            existingModal.size = FloaterSize.Small
        }
    }
    const [orientation] = getFloaterOrientation()
    const currentLocation = existingModal.locations[orientation]
    const [newLocation] = minimize
        ? [currentLocation]
        : getNewExpandLocation(modalKey, type)

    setMultiModal(
        modalKey,
        {
            ...existingModal,
            minimized: minimize,
            locations: {
                ...existingModal.locations,
                [orientation]: newLocation,
            },
        },
        { immediate: true, skipStack: true },
    )
    setModalStack({ modalKey }, { pushDown: minimize, immediate: true })
}

const isBlockedMemberCam = (id: number) => {
    if (!isMemberCam(id) || canViewerWatchMemberCams()) return false
    if (APPSTATE.app.authenticated) openTokensDrawer()
    else openSignupDrawer()
    return true
}

export const toggleFloatingRoomVideo = (override?: boolean) => {
    const current = getModalState().floating_room
    const hideVideo = override ?? !current.hideVideo
    if (
        !hideVideo &&
        current.roomView === RoomView.PM &&
        isBlockedMemberCam(current.id)
    ) {
        return
    }
    setModal(
        ModalKind.FloatingRoom,
        { ...current, show: true, hideVideo },
        { immediate: true },
    )
    if (hideVideo === current.hideVideo) return
    if (current.roomView === RoomView.PM) {
        syncPmVideo(current.id, !hideVideo)
        return
    }
    if (
        !hideVideo &&
        current.roomView === RoomView.Room &&
        isMemberWebcamRoom(current.id) &&
        !isOwnWebcamRoom(current.id)
    ) {
        initVideo(current.id)
    }
}

const partPmPreview = () => {
    if (pmPreview.id === null) return
    messageAction(MessageAction.VIDEO_FEED, {
        id: pmPreview.id,
        viewing: false,
    })
    pmPreview.id = null
}

const syncPmVideo = (id: number, viewing: boolean) => {
    if (!viewing) {
        if (pmPreview.id === id) partPmPreview()
        return
    }
    if (!isMemberCam(id) || !canViewerWatchMemberCams()) return
    if (pmPreview.id !== id) {
        partPmPreview()
        messageAction(MessageAction.VIDEO_FEED, { id, viewing: true })
        pmPreview.id = id
    }
    initVideo(id)
    pubSubUsers.emit(UserEvent.UPDATE + '::' + id)
}

export const updateFloatingRoom = (
    updates: Partial<FloatingRoomModal>,
    immediate = true,
) => {
    setModal(
        ModalKind.FloatingRoom,
        {
            ...getModalState().floating_room,
            ...updates,
        },
        { immediate },
    )
}

export const toggleQuickReplySize = (id: number) => {
    const modalKey = generateModalKey(QUICK_REPLY, id)
    const existingModal = getModalState().floater[modalKey]
    setMultiModal(modalKey, {
        ...existingModal,
        size:
            existingModal.size < FloaterSize.Large
                ? FloaterSize.Large
                : FloaterSize.Small,
    })
}

export const updateRoomCalculations = (immediate = true) => {
    const floatingRoom = getModalState().floating_room
    const calculations = recalcRoom({
        viewMode: floatingRoom.viewMode,
        roomView: floatingRoom.roomView,
        expanded: floatingRoom.expanded,
    })
    // unchanged groups keep their identity for the selectors reading them
    const changed = pickBy(
        calculations,
        (value, key) => !isEqual(value, floatingRoom[key]),
    )
    if (isEmpty(changed)) return
    setModal(
        ModalKind.FloatingRoom,
        { ...floatingRoom, ...changed },
        { immediate, skipStack: true },
    )
}

export const openFloatingRoom = (
    idOrSlug: number | string,
    roomView = RoomView.Room, // this may be too naive
) => {
    const [, id] = getSlugAndId(idOrSlug)
    const floatingRoom = getModalState().floating_room
    const currentId = floatingRoom.id
    const room = getRoomsState()[id]
    const videoID = room?.isLounge ? room.loungeSurrogateID : id
    const {
        isBroadcasting = false,
        isAway = false,
        isModel = false,
    } = getUser(videoID) || {}
    const isPM = roomView === RoomView.PM
    const freshOpen = currentId !== id || !floatingRoom.show
    const videoHidden = isPM
        ? !isModel || !isBroadcasting || isAway
        : isOwnWebcamRoom(id)
          ? currentId === id && floatingRoom.hideVideo
          : isMemberWebcamRoom(id)
            ? freshOpen || floatingRoom.hideVideo
            : currentId === id
              ? floatingRoom.hideVideo
              : false
    const showZoomVideo = isBroadcasting ? floatingRoom.showZoomVideo : false
    const calculations = recalcRoom({
        roomView,
        viewMode: floatingRoom.viewMode,
        expanded: true,
    })

    const newRoom: FloatingRoomModal = {
        id,
        show: true,
        type: FLOATING_ROOM,
        expanded: true,
        hideVideo: videoHidden,
        showTips: false,
        showLoungePreviews: floatingRoom.showLoungePreviews,
        minimized: false,
        roomView,
        viewMode: floatingRoom.viewMode,
        restoreViewMode: floatingRoom.restoreViewMode,
        reopenViewMode: null,
        showZoomVideo,
        avatarUrl: avatarUrl(id, 300),
        ...calculations,
    }

    if (pmPreview.id !== null && pmPreview.id !== id) partPmPreview()

    closeMiniPlayers()
    setModal(ModalKind.FloatingRoom, newRoom, {
        immediate: true,
    })

    syncPmVideo(id, isPM && !videoHidden)

    // a minimize set mobile theater aside; a room or PM reopening
    // with video takes it back, displaced for the current orientation the
    // way a rotation would displace it (auto full window, theater pending)
    if (
        floatingRoom.reopenViewMode !== null &&
        isBroadcasting &&
        roomView !== RoomView.Profile
    ) {
        const { portrait } = getScreenState()
        const isMobile = APPSTATE.app.isMobile
        const { fullWindow: fullWindowSetting } =
            SETTINGSSTATE[SettingsType.DEFAULT]
        const next = resolveOrientationViewMode({
            viewMode: floatingRoom.reopenViewMode,
            restoreViewMode: null,
            isVertical: portrait,
            isMobile,
            autoFullWindow:
                fullWindowSetting !== false && isMobile && !portrait,
        })
        setRoomViewMode(next.viewMode, false, {
            restoreViewMode: next.restoreViewMode,
        })
    }
}

export const closeFloatingRoom = (
    idOrSlug: string | number,
    { skipClose = false } = {},
) => {
    const [, id] = getSlugAndId(idOrSlug)

    if (getModalState().floating_room.id === id) {
        syncPmVideo(id, false)
        if (!skipClose) closeRoom(id, { skipCloseIfPinned: true })
        const container = VIDEOSTATE.videos[id]?.container
        if (container && container.firstElementChild)
            container.firstElementChild.classList.remove(
                'video-transition-source',
            )

        setModal(
            ModalKind.FloatingRoom,
            {
                ...getModalState().floating_room,
                showLoungePreviews: false,
                showZoomVideo: false,
                restoreViewMode: null,
                reopenViewMode: null,
                minimized: false,
                show: false,
            },
            { immediate: true },
        )
        closeMiniPlayers('room')
    }
}

export const isFloatingRoomPm = (idOrSlug: string | number) => {
    const [, id] = getSlugAndId(idOrSlug)

    if (getModalState().floating_room.id === id) {
        return getModalState().floating_room.roomView === RoomView.PM
    }
    return false
}

/* swap between fullscreen or content area sizes for the floating room  */
export const setFullScreenExpandedState = (fullScreen: boolean) => {
    const floatingRoom = getModalState().floating_room
    const { portrait } = getScreenState()
    const fullWindow = floatingRoom.viewMode === ViewMode.FullWindow
    const newExpanded =
        fullWindow || APPSTATE.app.isMobile || portrait
            ? true
            : (fullScreen ?? floatingRoom.expanded)

    const updatedRoomState = {
        ...floatingRoom,
        expanded: newExpanded,
    }
    setModal(ModalKind.FloatingRoom, updatedRoomState, { immediate: false })
    updateRoomCalculations()
}
/* zoom the portrait video (mobile); mobile theater is a view mode, not a zoom level */
export const setZoomVideoState = (showZoomVideo: boolean) => {
    setModal(
        ModalKind.FloatingRoom,
        { ...getModalState().floating_room, showZoomVideo },
        { immediate: true },
    )
}

/** A model's live player in a floater, carried from a minimized menu or room */
export const openMiniPlayer = (
    videoID: number,
    origin: MiniPlayerOrigin,
    coords?: { x: number; y: number },
) => {
    const modalKey = generateModalKey(MINI_PLAYER, videoID)
    const previewLocation = calcPreviewLocation(FloaterSize.Small)
    const [location] = coords
        ? getNewExpandLocation(modalKey, MINI_PLAYER, FloaterSize.Small, {
              ...previewLocation,
              ...coords,
          })
        : [previewLocation]
    setMultiModal(
        modalKey,
        {
            id: videoID,
            type: MINI_PLAYER,
            show: true,
            minimized: false,
            origin,
            shrinkToEdge: false,
            size: FloaterSize.Small,
            close: true,
            sticky: true,
            stickyHeader: true,
            locations: {
                portrait: { ...location },
                landscape: { ...location },
            },
        },
        { immediate: true },
    )
}

/** Remove every mini player, or those of one origin; a room stays joined */
export const closeMiniPlayers = (kind?: MiniPlayerOrigin['kind']) =>
    Object.entries(getModalState().floater).forEach(([modalKey, floater]) => {
        if (floater.type !== MINI_PLAYER) return
        if (kind && floater.origin?.kind !== kind) return
        removeMultiModal(modalKey)
    })

export const setMiniPlayerShrinkToEdge = (
    videoID: number,
    shrinkToEdge: Edge | false,
) => {
    const modalKey = generateModalKey(MINI_PLAYER, videoID)
    const floater = getModalState().floater[modalKey]
    if (!floater) return
    setMultiModal(
        modalKey,
        { ...floater, shrinkToEdge },
        { immediate: true, skipStack: true },
    )
}

/** A minimized lounge's player follows its new surrogate; a model that
 *  already has a player keeps it, and the lounge's stays where it was */
export const retargetMiniPlayer = (fromID: number, toID: number) => {
    const floaters = getModalState().floater
    const fromKey = generateModalKey(MINI_PLAYER, fromID)
    const toKey = generateModalKey(MINI_PLAYER, toID)
    const player = floaters[fromKey]
    if (!player || !toID || floaters[toKey]) return
    removeMultiModal(fromKey)
    setMultiModal(toKey, { ...player, id: toID }, { immediate: true })
}

/** A minimized room's player: a lounge's is its surrogate's */
export const openRoomMiniPlayer = (
    roomID: number,
    coords?: { x: number; y: number },
) => {
    const room = getRoomsState()[roomID]
    const videoID = room?.isLounge ? room.loungeSurrogateID : roomID
    if (!videoID) return
    openMiniPlayer(videoID, { kind: 'room', roomID }, coords)
}

type MinimizeFloatingRoomOptions = {
    /** The mode the minimize sets aside for the room's reopen (mobile theater) */
    reopenViewMode?: ViewMode | null
}

/**
 * The room leaves the screen but stays joined, behind a room-origin mini
 * player dropped at `coords` (none while the floating player is off). A PM
 * without video leaves its quick reply instead, and closes.
 */
export const minimizeFloatingRoom = (
    idOrSlug: number | string,
    coords?: { x: number; y: number },
    { reopenViewMode = null }: MinimizeFloatingRoomOptions = {},
) => {
    const [, id] = getSlugAndId(idOrSlug)
    const { [id]: { isIntentionallyClosed = false } = {} } = PMSTATE.chats
    const floatingRoom = getModalState().floating_room
    const isPM = floatingRoom.roomView === RoomView.PM
    // not for a conversation the viewer closed, nor on a phone in landscape,
    // where openQuickReply routes back into the PM this is leaving
    const canQuickReply =
        isPM &&
        !isIntentionallyClosed &&
        !(APPSTATE.app.isMobile && !getScreenState().portrait)

    const minimize = () => {
        setModal(
            ModalKind.FloatingRoom,
            {
                ...floatingRoom,
                restoreViewMode: null,
                reopenViewMode,
                id,
                show: true,
                minimized: true,
            },
            { immediate: true },
        )
        if (SETTINGSSTATE[SettingsType.DEFAULT].enableFloatingPreview)
            openRoomMiniPlayer(id, coords)
    }

    if (isPM && floatingRoom.hideVideo) {
        if (canQuickReply) openQuickReply(id, { size: FloaterSize.Small })
        closeFloatingRoom(id)
        return
    }

    // a desktop PM keeps its quick reply too: beside the player in a
    // non-public show, alone in a public one (the quick reply plays it)
    if (canQuickReply && !isTouchDevice) {
        const { isPublicShow, isBroadcasting } = getUser(id) || {}
        if (!isPublicShow && isBroadcasting) {
            minimize()
            openQuickReply(id, { size: FloaterSize.Small })
        } else {
            openQuickReply(id)
            closeFloatingRoom(id)
        }
        return
    }
    minimize()
}
